import { useEffect, useState } from 'react';
import { useAuth } from './lib/auth.jsx';
import Login from './pages/Login.jsx';
import Suppliers from './pages/Suppliers.jsx';
import ImportPI from './pages/ImportPI.jsx';
import Scan from './pages/Scan.jsx';
import PurchaseOrders from './pages/PurchaseOrders.jsx';
import ByPhoto from './pages/ByPhoto.jsx';
import Stats from './pages/Stats.jsx';
import Users from './pages/Users.jsx';
import ProductDrawer from './components/ProductDrawer.jsx';
import { supabase } from './lib/supabase.js';

// ลำดับเมนูตามที่ Purim จัด 1 ส.ค.: ใบสั่งซื้อทั้งหมดขึ้นหน้าสุด · สร้างสินค้าใหม่ไปอยู่หน้าซัพพลายเออร์
// รายการที่มี action = ปุ่มเปิดลิ้นชัก ไม่ใช่หน้า (ไม่ต้องอยู่ใน PAGES / ไม่มีสถานะ on)
const NAV = [
  { key: 'orders', label: 'ใบสั่งซื้อทั้งหมด' },
  { key: 'import', label: 'by PI' },
  { key: 'photo', label: 'by Photos' },
  { key: 'scan', label: 'by iPhone' },
  { key: 'new-product', label: 'สร้างสินค้าใหม่', action: 'newProduct', costOnly: true },
  { key: 'suppliers', label: 'ซัพพลายเออร์' },
  { key: 'stats', label: 'สถิติ' },
];

// 'scan' = หน้ามือถือ (เพิ่มไปยังหน้าจอโฮมแล้วเปิดเต็มจอ) — อยู่ใน PAGES แต่ไม่อยู่ใน NAV เหมือน 'users'
// 'buy' (QUICK BUY) ยุบเข้าหน้า "ใบสั่งซื้อ" แล้ว 1 ส.ค. — ลิงก์เก่า #buy เด้งไปที่นั่นแทน
const PAGES = ['import', 'photo', 'orders', 'suppliers', 'stats', 'users', 'scan'];
const pageAlias = p => (p === 'buy' ? 'orders' : p);
const pageFromHash = () => {
  const p = pageAlias(window.location.hash.slice(1).split('/')[0]);
  return PAGES.includes(p) ? p : 'import';
};

export default function App() {
  const { session, profile, signOut, hasCostAccess } = useAuth();
  const [page, setPageRaw] = useState(pageFromHash);
  const [newProduct, setNewProduct] = useState(false); // standalone create drawer
  const [changePw, setChangePw] = useState(false);

  // hash-based navigation so the browser back button works between pages
  const setPage = p => {
    if (p !== page) window.history.pushState(null, '', `#${p}`);
    setPageRaw(p);
  };
  useEffect(() => {
    const onPop = () => setPageRaw(pageFromHash());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (session === undefined) return null; // restoring session

  return (
    <div className="shell">
      {/* หน้าสแกนกินเต็มจอมือถือ — ซ่อน topbar (แต่ตอนยังไม่ล็อกอินต้องโชว์ เพราะหน้านั้นคือ Login) */}
      {(!session || page !== 'scan') && (
      <div className="topbar">
        <div className="brand-lockup" onClick={() => setPage('import')}>
          <img src="/camp-studio-logo.png" alt="CAMP STUDIO" className="cs-logo" />
          <div className="wordmark">SOURCEPOINT<span>จัดซื้อ</span></div>
        </div>
        {session && (
          <nav className="topnav">
            {NAV.map(n => {
              if (n.costOnly && !hasCostAccess) return null;
              return n.action === 'newProduct' ? (
                <a key={n.key} onClick={() => setNewProduct(true)}>{n.label}</a>
              ) : (
                <a key={n.key} className={page === n.key ? 'on' : ''} onClick={() => setPage(n.key)}>
                  {n.label}
                </a>
              );
            })}
            {profile?.role === 'manager' && (
              <a className={page === 'users' ? 'on' : ''} onClick={() => setPage('users')}>ผู้ใช้</a>
            )}
            <span className="whoami">{profile?.full_name ?? session.user.email}</span>
            <a onClick={() => setChangePw(true)}>รหัสผ่าน</a>
            <a onClick={signOut}>ออก</a>
          </nav>
        )}
      </div>
      )}

      {!session ? (
        <Login />
      ) : page === 'suppliers' ? (
        <Suppliers />
      ) : page === 'import' ? (
        <ImportPI onGoSuppliers={() => setPage('suppliers')} onGoOrders={() => setPage('orders')} />
      ) : page === 'photo' ? (
        <ByPhoto />
      ) : page === 'orders' ? (
        <PurchaseOrders />
      ) : page === 'users' ? (
        <Users />
      ) : page === 'scan' ? (
        <Scan onExit={() => setPage('orders')} />
      ) : (
        <Stats />
      )}

      {newProduct && (
        <ProductDrawer onClose={() => setNewProduct(false)} onSaved={() => setNewProduct(false)} />
      )}
      {changePw && <ChangePasswordDrawer onClose={() => setChangePw(false)} />}
    </div>
  );
}

// Everyone can change their own password (admin hands out an initial one).
function ChangePasswordDrawer({ onClose }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (busy) return;
    if (pw.length < 8) { setMsg('รหัสผ่านอย่างน้อย 8 ตัวอักษร'); return; }
    if (pw !== pw2) { setMsg('รหัสผ่านสองช่องไม่ตรงกัน'); return; }
    setBusy(true); setMsg('');
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) setMsg(error.message);
    else { setMsg(''); onClose(); }
  }

  return (
    <>
      <div className="scrim open" onClick={onClose} />
      <aside className="drawer open">
        <div className="drawer-head">
          <div>
            <div className="eyebrow">บัญชีของฉัน · My account</div>
            <h2>เปลี่ยนรหัสผ่าน</h2>
          </div>
          <button className="xbtn" onClick={onClose} aria-label="ปิด">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
          </button>
        </div>
        <div className="drawer-body">
          <div className="field">
            <div className="lbl"><span className="th">รหัสผ่านใหม่</span><span className="opt">อย่างน้อย 8 ตัว</span></div>
            <input className="under" type="password" autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)} />
          </div>
          <div className="field">
            <div className="lbl"><span className="th">ยืนยันรหัสผ่านใหม่</span></div>
            <input className="under" type="password" autoComplete="new-password" value={pw2} onChange={e => setPw2(e.target.value)} />
          </div>
          <p className="err-note">{msg}</p>
        </div>
        <div className="drawer-foot">
          <span className="txt">บันทึกรหัสผ่าน</span>
          <button className="arrow" onClick={save} disabled={busy} aria-label="บันทึก">
            <svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
          </button>
        </div>
      </aside>
    </>
  );
}
